import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import useCallApiAndLoad from '../../../hooks/useCallApiAndLoad';
import useAuth from '../../../hooks/useAuth';
import useProperList from '../../../hooks/useProperList';
import useSdrList from '../../../hooks/useSdrList';
import { fetchResponseAdapter } from '../../../adapters/fetchAdapter';
import { listToSelectOptionAdapter } from '../../../adapters/listAdapter';
import { getDateVars } from '../../../helpers/dateHelpers';
import {
  getHoursMinutesFromMinutes,
  getStringDate,
} from '../../../adapters/dateAdapter';
import { Option } from '../../../models/formModel';
import { VisitPreSaleState } from '../../../constants/visitsConstants';
import {
  buildCalendarVisitInvestorData,
  buildCalendarVisitInvestorEvent,
} from '../adapters/visitsInvestorAdapter';
import {
  createProjectVisitsEndpoint,
  deleteProjectVisitsEndpoint,
  getAvailableTimeProjectVisitsEndpoint,
  getProjectVisitsEndpoint,
  markVisitPreSaleDoneEndpoint,
  updateProjectVisitsEndpoint,
  getCancellationReasons,
} from '../services/visitsInvestorService';
import { getLandlordByMailEndpoint } from '../../../services/landlordService';
import {
  CreateVisitInvestor,
  ProjectVisitList,
  UpdateVisitInvestor,
} from '../models/visitsInvestorModel';
import { UseVisitCalendarConfig } from '../../../hooks/useVisitCalendar';

export const useManagerInvestorVisitsCalendar = () => {
  const navigate = useNavigate();
  const { id: projectId = '' } = useParams();
  const { callEndpoint } = useCallApiAndLoad();
  const { getAuthToken } = useAuth();
  const token = getAuthToken();

  const { propersOptions } = useProperList('for_pre_sale');
  const { sdrOptions } = useSdrList('SDR_PRESALES');

  // Investor search
  const [landlordEmail, setLandlordEmail] = useState<string>('');
  const [landlordFound, setLandlordFound] = useState<any>(null);
  const [formError, setFormError] = useState<string>('');

  // Modals
  const [modalAdd, setModalAdd] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [modalView, setModalView] = useState(false);
  const [modalDoneCancelled, setModalDoneCancelled] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  // Cancellation reasons
  const [cancellationReasons, setCancellationReasons] = useState<string[]>([]);

  // Date / hour
  const [startDate, setStartDate] = useState('');
  const [oldStartDate, setOldStartDate] = useState('');
  const [creationStartDate, setCreationStartDate] = useState<Date>(new Date());
  const [hours, setHours] = useState<Option[]>([]);

  // Calendar event state
  const [event, setEvent] = useState<any>(null);
  const [oldEvent, setOldEvent] = useState<any>(null);
  const [changeEvent, setChangeEvent] = useState<any>(null);

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors },
    unregister,
  } = useForm<CreateVisitInvestor>({
    resolver: yupResolver(
      yup.object({
        start_time: yup.number().required('Hora de visita es requerida'),
        proper_id: yup.string().required('Proper es requerido'),
      }),
    ),
  });

  const loadHours = async (date: string) => {
    if (!date) {
      setHours([]);
      return;
    }
    const { status, data } = await callEndpoint(
      getAvailableTimeProjectVisitsEndpoint(projectId, date, token),
    );
    if (status === 200) {
      const response = fetchResponseAdapter(data);
      setHours(
        listToSelectOptionAdapter(
          response.data,
          'minutes',
          'time',
          true,
          'is_blocked',
        ),
      );
    }
  };

  const handleSearchLandlord = async () => {
    if (!landlordEmail) {
      setFormError('Debes ingresar un correo valido');
      setLandlordFound(null);
      return;
    }
    setFormError('');
    const { status, data } = await callEndpoint(
      getLandlordByMailEndpoint(landlordEmail.trim().toLowerCase(), token),
    );
    if (status === 200) {
      setLandlordFound(fetchResponseAdapter(data).data);
      return;
    }
    setFormError('Inquilino no encontrado');
    setLandlordFound(null);
  };

  const handleOpenDeleteModal = async () => {
    try {
      const { status, data } = await callEndpoint(getCancellationReasons(token));
      if (status === 200) {
        const response = fetchResponseAdapter(data);
        setCancellationReasons(Array.isArray(response.data) ? response.data : []);
      }
    } catch {
      setCancellationReasons([]);
    }
    setDeleteModalOpen(true);
  };

  const closeAddEditModal = () => {
    setModalAdd(false);
    if (changeEvent !== null) changeEvent.revert();
  };

  // FullCalendar callbacks — passed to useVisitCalendar
  const fetchEvents: UseVisitCalendarConfig['fetchEvents'] = (
    start,
    end,
    successCallback,
    failureCallback,
  ) => {
    const { day: sd, month: sm, year: sy } = getDateVars(start);
    const { day: ed, month: em, year: ey } = getDateVars(end);
    callEndpoint(
      getProjectVisitsEndpoint(
        projectId,
        `${sy}-${sm}-${sd}`,
        `${ey}-${em}-${ed}`,
        token,
      ),
    )
      .then(({ status, data }) => {
        if (status === 200) {
          successCallback(
            buildCalendarVisitInvestorData(fetchResponseAdapter(data).data),
          );
        } else {
          failureCallback(new Error('Error al cargar información'));
        }
      })
      .catch(failureCallback);
  };

  const onSelect: UseVisitCalendarConfig['onSelect'] = (info) => {
    setCreationStartDate(info.start);
    setModalAdd(true);
    setIsEdit(false);
    setStartDate(info.startStr);
    loadHours(info.startStr);
    setLandlordFound(null);
    setEvent(null);
    setOldEvent(null);
    setOldStartDate('');
    setChangeEvent(null);
    unregister(['start_time', 'proper_id', 'landlord_id', 'date']);
    setFormError('');
  };

  const onEventClick: UseVisitCalendarConfig['onEventClick'] = ({ event }) => {
    setEvent(event);
    if (
      event.extendedProps.status === VisitPreSaleState.DELETED ||
      event.extendedProps.status === VisitPreSaleState.VOIDED ||
      event.extendedProps.status === VisitPreSaleState.DONE
    ) {
      setModalDoneCancelled(true);
    } else {
      setModalView(true);
    }
  };

  const onEventChange: UseVisitCalendarConfig['onEventChange'] = (
    eventChange,
  ) => {
    const { event: changedEvent, oldEvent: prevEvent } = eventChange;
    if (
      changedEvent.extendedProps.status === VisitPreSaleState.DELETED ||
      changedEvent.extendedProps.status === VisitPreSaleState.VOIDED ||
      changedEvent.extendedProps.status === VisitPreSaleState.DONE
    ) {
      return;
    }
    setModalAdd(true);
    setIsEdit(true);
    setStartDate(changedEvent.startStr);
    loadHours(getStringDate(changedEvent.start));
    setLandlordFound({
      avatar: changedEvent.extendedProps.landlordInfo?.avatar,
      name: changedEvent.extendedProps.landlordInfo?.name,
      phone: '',
    });
    setValue('proper_id', changedEvent.extendedProps.proper_id);
    setEvent(changedEvent);
    setOldEvent(prevEvent);
    setOldStartDate(prevEvent.startStr);
    setChangeEvent(eventChange);
    unregister(['start_time']);
    setFormError('');
  };

  // doCreateVisit is a factory: receives addCalendarEvent from the page (which gets it from
  // useVisitCalendar) to avoid coupling this hook to the calendar instance.
  const doCreateVisit = (addCalendarEvent: (e: any) => void) =>
    handleSubmit(async (form: CreateVisitInvestor) => {
      setFormError('');
      if (!landlordFound?.landlord_id) {
        setFormError('Información de inquilino es requerida');
        return;
      }
      if (!startDate) {
        setFormError('No fue posible seleccionar el día para la visita');
        return;
      }
      const formApi: CreateVisitInvestor = {
        project_id: projectId,
        landlord_id: landlordFound.landlord_id,
        date: startDate,
        start_time: form.start_time,
        visit_type: 'virtual',
        proper_id: form.proper_id,
      };
      const { status, data } = await callEndpoint(
        createProjectVisitsEndpoint(formApi, token),
      );
      if (status === 201) {
        const { hours: h, minutes: m } = getHoursMinutesFromMinutes(
          form.start_time,
        );
        const [yearStr, monthStr, dayStr] = startDate.split('-');
        const dateTimeVars = {
          day: Number.parseInt(dayStr),
          month: Number.parseInt(monthStr),
          year: Number.parseInt(yearStr),
          hours: h,
          minutes: m,
        };
        const response = fetchResponseAdapter(data);
        const visit: ProjectVisitList = {
          id: response.data.id,
          project_id: projectId,
          status: VisitPreSaleState.SCHEDULED,
          visitDateTime: dateTimeVars,
          landlordInfo: {
            id: landlordFound.landlord_id,
            name: landlordFound.name,
            avatar: landlordFound.avatar,
          },
          guestInfo: null,
          proper_id: form.proper_id,
          sdr_id: null,
        };
        addCalendarEvent(buildCalendarVisitInvestorEvent(visit, dateTimeVars));
        setModalAdd(false);
        return;
      }
      setFormError(`(${status}) - No fue posible agendar la visita`);
    });

  const doRescheduleVisit = handleSubmit(async (form: CreateVisitInvestor) => {
    setFormError('');
    const { id: visitId, start } = event;
    if (!visitId || !start) {
      setFormError(
        'No fue posible seleccionar el día para reagendar la visita',
      );
      return;
    }
    const formApi: UpdateVisitInvestor = {
      date: getStringDate(start),
      start_time: form.start_time,
    };
    const { status } = await callEndpoint(
      updateProjectVisitsEndpoint(visitId, formApi, token),
    );
    if (status === 200) {
      setModalAdd(false);
      navigate(0);
      return;
    }
    setFormError(`(${status}) - No fue posible reagendar la visita`);
  });

  const handleDeleteVisit = async (reason: string, explain_reason: string, visitStatus: string) => {
    if (!event) return;
    const { status } = await callEndpoint(
      deleteProjectVisitsEndpoint(event.id, token, reason, explain_reason, visitStatus),
    );
    if (status === 200) {
      setModalView(false);
      event.setExtendedProp(
        'status',
        visitStatus === VisitPreSaleState.VOIDED
          ? VisitPreSaleState.VOIDED
          : VisitPreSaleState.DELETED,
      );
      event.setProp('classNames', 'bg-red text-white');
    }
    setDeleteModalOpen(false);
  };

  const handleCompleteVisit = async () => {
    if (!event) return;
    const { status } = await callEndpoint(
      markVisitPreSaleDoneEndpoint(event.id, token),
    );
    if (status === 200) {
      setModalView(false);
      event.setExtendedProp('status', VisitPreSaleState.DONE);
      event.setProp('classNames', 'bg-green text-white');
    }
  };

  return {
    // investor search
    landlordEmail,
    setLandlordEmail,
    landlordFound,
    setLandlordFound,
    handleSearchLandlord,
    formError,
    setFormError,
    // modals
    modalAdd,
    setModalAdd,
    isEdit,
    setIsEdit,
    modalView,
    setModalView,
    modalDoneCancelled,
    setModalDoneCancelled,
    deleteModalOpen,
    setDeleteModalOpen,
    closeAddEditModal,
    handleOpenDeleteModal,
    cancellationReasons,
    // date
    startDate,
    setStartDate,
    oldStartDate,
    creationStartDate,
    hours,
    loadHours,
    // calendar callbacks (passed to useVisitCalendar)
    fetchEvents,
    onSelect,
    onEventClick,
    onEventChange,
    // selected event
    event,
    oldEvent,
    // form
    control,
    setValue,
    errors,
    unregister,
    doCreateVisit,
    doRescheduleVisit,
    handleDeleteVisit,
    handleCompleteVisit,
    // lists
    propersOptions,
    sdrOptions,
  };
};
